import React, { useState, useContext, useEffect, useRef } from "react";
import logo from "../Assets/fabhub-logo-nav.png";
import { backend_url } from "../../App";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ShopContext } from "../../Context/ShopContext";

// Set to false to re-enable the live Razorpay checkout.
const DUMMY_PAYMENT_MODE = true;

const OrderWithCartsPopup = ({
  showPopup,
  cartItems,
  currency,
  closePopup,
}) => {
  const [step, setStep] = useState(1);
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const { clearCart } = useContext(ShopContext);
  const [loading, setLoading] = useState(false);

  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    address: "",
    city: "",
    pincode: "",
    state: "",
    country: "",
  });

  const indianStates = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const token = localStorage.getItem("auth-token"); // match your existing login flow
        if (!token) return; // if not logged in, skip prefill

        const res = await fetch(`${backend_url}/getuserdetails`, {
          headers: { "auth-token": token },
        });

        const data = await res.json();
        if (data.success && data.user) {
          setUserInfo({
            userId: data.user.userId,
            name: data.user.name || "",
            email: data.user.email || "",
            phoneNumber: data.user.phoneNumber || "",
            address: data.user.address || "",
            city: data.user.city || "",
            pincode: data.user.pincode || "",
            state: data.user.state || "",
            country: data.user.country || "",
          });
        }
      } catch (err) {
        console.error("Error fetching user details in order popup", err);
      }
    };

    if (showPopup) {
      fetchUserDetails();
    }
  }, [showPopup]);

  // Calculate subtotal from cartItems
  const subtotal = (Array.isArray(cartItems) ? cartItems : []).reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  // Apply promo discount
  const discountPercent = appliedPromo?.discount || 0;
  const discountAmount = (subtotal * discountPercent) / 100;
  const discountedPrice = subtotal - discountAmount;

  // Shipping and GST
  const GST_RATE = 0.05; // 5% GST for textiles/fabrics
  const shipping = discountedPrice >= 999 ? 0 : 49;
  const gst = Number(((discountedPrice + shipping) * GST_RATE).toFixed(2));

  // Final total price
  const finalTotalPrice = Number((discountedPrice + shipping + gst).toFixed(2));

  const popupRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closePopup(); // 👈 Close when clicking outside
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup, closePopup]);

  if (!showPopup) return null;

  const handleNext = () => {

     if (
      !userInfo.name ||
      !userInfo.email ||
      !userInfo.phoneNumber ||
      !userInfo.address ||
      !userInfo.pincode ||
      !userInfo.city ||
      !userInfo.state ||
      !userInfo.country
    ) {
      toast.error("Please fill all required fields!");
      return;
    }

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email);
    const isValidPhone = /^\d{10}$/.test(userInfo.phoneNumber);

    if (!isValidEmail) {
      toast.error("Invalid email");
      return;
    }
    if (!isValidPhone) {
      toast.error("Invalid phone number");
      return;
    }


    handleOrder();
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }
    try {
      const res = await fetch(`${backend_url}/api/checkpromocode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ promoCode: promoCode.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setAppliedPromo({
          promoCode: promoCode.trim(),
          discount: data.discount,
        });
        setPromoError("");
        toast.success(`Promo applied: ${data.discount}% off`);
      } else {
        setAppliedPromo(null);
        setPromoError(data.message || "Invalid promo code");
      }
    } catch (err) {
      setPromoError("Something went wrong");
    }
  };

  const handleOrderComplete = () => {
    closePopup();
    window.location.reload();
  };

  const handleOrder = async () => {
    try {
      setLoading(true);

      if (!cartItems || cartItems.length === 0) {
        toast.error("Your cart is empty!");
        setLoading(false);
        return;
      }

      // Use the exact same rules as the on-screen summary so the customer
      // is billed what they were shown: shipping ₹49 (free ≥ ₹999), 5% GST.
      const orderShipping = shipping;
      const gstPercent = GST_RATE * 100;
      const shippingPerItem = orderShipping / cartItems.length;

      const itemsToProcess = cartItems.map((item) => {
        const itemTotal = item.price * item.quantity;
        const discountAmount = appliedPromo
          ? (itemTotal * appliedPromo.discount) / 100
          : 0;
        const totalAfterDiscount = itemTotal - discountAmount;

        const gstAmount =
          (totalAfterDiscount + shippingPerItem) * (gstPercent / 100);
        const finalTotal = totalAfterDiscount + shippingPerItem + gstAmount;

        return {
          productId: item.itemId || item.productId,
          size: item.size || "None",
          quantity: item.quantity,
          pricePerUnit: item.price,
          itemTotal: parseFloat(itemTotal.toFixed(2)),
          discountAmount: parseFloat(discountAmount.toFixed(2)),
          shippingCharges: parseFloat(shippingPerItem.toFixed(2)),
          gstAmount: parseFloat(gstAmount.toFixed(2)),
          totalPrice: parseFloat(finalTotal.toFixed(2))
        };
      });

      // Grand total matches the summary shown to the customer
      const totalAmount = finalTotalPrice;

      const orderData = {
        userId: userInfo.userId,
        name: userInfo.name || "N/A",
        phoneNumber: userInfo.phoneNumber,
        address: userInfo.address,
        email: userInfo.email,
        city: userInfo.city,
        pincode: userInfo.pincode,
        state: userInfo.state,
        country: userInfo.country,
        cartItems: itemsToProcess,
        totalAmount, // send totalAmount to backend as well
      };

      // Create order in backend
      const { data } = await axios.post(
        `${backend_url}/create-order`,
        orderData
      );

      if (!data.success) {
        toast.error("Failed to initiate order");
        setLoading(false);
        return;
      }

      // Dummy checkout mode: skip live Razorpay to test the order flow end-to-end.
      // Flip DUMMY_PAYMENT_MODE to false to restore the real Razorpay checkout below.
      if (DUMMY_PAYMENT_MODE) {
        toast.success("Dummy order placed successfully! (Razorpay skipped)");
        clearCart();
        setStep(3);
        setLoading(false);
        return;
      }

      const razorpayAmount = data.razorpayOrder?.amount || totalAmount;

      const options = {
        key: "rzp_live_RKAmIJLBB82iLJ",
        amount: razorpayAmount,
        currency: "INR",
        name: "AavryaaFabhub",
        description: "Cart Payment",
        image: logo,
        order_id: data.razorpayOrder?.id,
        handler: async function (response) {
          try {
            const res = await axios.post(`${backend_url}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (res.data.success) {
              toast.success("Payment successful and order placed!");
              clearCart();
              setStep(3);
            } else {
              toast.error("Payment verification failed");
            }
          } catch (err) {
            console.error("Error verifying payment:", err);
            toast.error("Error during payment verification");
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: userInfo.name,
          email: userInfo.email,
          contact: userInfo.phoneNumber,
        },
        theme: { color: "#A565E9" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Error in handleOrder:", error);
      toast.error("Failed to place order.");
      setLoading(false);
    }
  };

  if (!showPopup) return null;

  return (
<>
  <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start z-50 p-4">
    <div className="min-h-full w-full flex items-center justify-center">
      <div
        className="bg-white rounded-xl shadow-lg overflow-hidden w-full max-w-6xl my-8 max-h-[90vh] overflow-y-auto"
        ref={popupRef}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white p-6 flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-center">
            <img
              crossOrigin="anonymous"
              src={logo}
              alt="Logo"
              className="h-10 mr-4"
            />
            <h1 className="text-2xl font-bold">
              {step !== 3 ? "Complete Your Purchase" : "Order Confirmed!"}
            </h1>
          </div>
          <button
            onClick={closePopup}
            className="text-white hover:text-purple-200 transition-colors"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {step !== 3 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - User Information */}
              <div className="bg-white rounded-lg p-6">
                <h2 className="text-xl font-bold text-purple-700 mb-4 border-b border-purple-200 pb-2">
                  Your Information
                </h2>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter Promo Code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      className="flex-1 border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                    />
                    <button
                      onClick={applyPromoCode}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-3 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center text-sm whitespace-nowrap"
                    >
                      Apply
                    </button>
                  </div>
                  {promoError && (
                    <p className="text-red-500 text-sm font-medium px-2">
                      {promoError}
                    </p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        placeholder="John Doe"
                        value={userInfo.name}
                        onChange={(e) =>
                          setUserInfo({ ...userInfo, name: e.target.value })
                        }
                        className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        placeholder="john@example.com"
                        value={userInfo.email}
                        onChange={(e) =>
                          setUserInfo({ ...userInfo, email: e.target.value })
                        }
                        className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      placeholder="1234567890"
                      value={userInfo.phoneNumber}
                      onChange={(e) => {
                        const inputVal = e.target.value;
                        if (/^\d*$/.test(inputVal) && inputVal.length <= 10) {
                          setUserInfo({ ...userInfo, phoneNumber: inputVal });
                        }
                      }}
                      className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address *
                    </label>
                    <input
                      type="text"
                      placeholder="123 Main St"
                      value={userInfo.address}
                      onChange={(e) =>
                        setUserInfo({ ...userInfo, address: e.target.value })
                      }
                      className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City *
                      </label>
                      <input
                        type="text"
                        placeholder="New York"
                        value={userInfo.city}
                        onChange={(e) =>
                          setUserInfo({ ...userInfo, city: e.target.value })
                        }
                        className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Postal Code *
                      </label>
                      <input
                        type="text"
                        placeholder="10001"
                        value={userInfo.pincode}
                        onChange={(e) =>
                          setUserInfo({
                            ...userInfo,
                            pincode: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6),
                          })
                        }
                        className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country *
                      </label>
                      <select
                        value={userInfo.country}
                        onChange={(e) =>
                          setUserInfo({
                            ...userInfo,
                            country: e.target.value,
                            state: "",
                          })
                        }
                        className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                      >
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    {userInfo.country === "India" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State *
                        </label>
                        <select
                          value={userInfo.state}
                          onChange={(e) =>
                            setUserInfo({
                              ...userInfo,
                              state: e.target.value,
                            })
                          }
                          className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                        >
                          <option value="">Select State</option>
                          {indianStates.map((state, idx) => (
                            <option key={idx} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-bold text-purple-700 mb-4 border-b border-purple-200 pb-2">
                  Order Summary
                </h2>

                {/* Product List */}
                <div className="mb-6">
                  {Array.isArray(cartItems) && cartItems.length > 0 ? (
                    cartItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-white rounded-lg shadow-sm border border-purple-200 gap-4 mb-3"
                      >
                        <div className="flex items-start sm:items-center gap-4 w-full sm:w-auto">
                          <img
                            crossOrigin="anonymous"
                            src={item.image}
                            alt={item.name}
                            className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md border flex-shrink-0"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 text-sm sm:text-base">
                              {item.name}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              Quantity: {item.quantity}
                            </p>
                          </div>
                        </div>
                        <p className="font-medium text-purple-700 text-sm sm:text-base mt-2 sm:mt-0">
                          {currency}
                          {(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="p-4 text-gray-500 text-center bg-white rounded-lg">
                      No products to show
                    </p>
                  )}
                </div>

                {/* Billing Summary */}
                <div className="bg-white rounded-lg shadow-sm border border-purple-200 p-5 space-y-3">
                  <h3 className="font-semibold text-gray-800 border-b border-gray-100 pb-2">
                    Order Details
                  </h3>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Subtotal:</span>
                    <span className="font-medium">
                      {currency}
                      {cartItems
                        .reduce((sum, item) => sum + item.price * item.quantity, 0)
                        .toFixed(2)}
                    </span>
                  </div>

                  {discountPercent > 0 && (
                    <div className="flex justify-between items-center text-green-700 font-medium">
                      <span>Promo Discount ({discountPercent}%):</span>
                      <span>
                        - {currency}
                        {discountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Shipping Charges:</span>
                    <span>
                      {currency}
                      {shipping.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">GST (5%):</span>
                    <span>
                      {currency}
                      {gst.toFixed(2)}
                    </span>
                  </div>

                  <div className="border-t border-gray-300 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-bold text-lg">
                        Grand Total:
                      </span>
                      <span className="text-purple-700 font-bold text-lg">
                        {currency}
                        {finalTotalPrice.toFixed(2)}
                      </span>
                    </div>
                    <button
                      onClick={handleNext}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-purple-600 to-purple-500 text-white py-3 px-6 rounded-lg font-medium hover:opacity-90 transition-all shadow-md hover:shadow-lg mt-4 flex items-center justify-center text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <span className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          Placing Order...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 5.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 10l1.293-1.293zm4 0a1 1 0 010 1.414L13.586 10l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Confirm Order
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm overflow-hidden p-6 sm:p-8 text-center animate-fade-in">
              {/* Animated checkmark */}
              <div className="relative w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6">
                <div className="absolute inset-0 bg-green-100 rounded-full animate-scale-in"></div>
                <svg
                  className="absolute inset-0 m-auto w-10 h-10 sm:w-16 sm:h-16 text-green-500 animate-checkmark"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 sm:mb-3 flex items-center justify-center">
                Order Confirmed!
                <span className="ml-2 animate-bounce">🎉</span>
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-4 sm:mb-6">
                Your order{" "}
                <span className="font-semibold text-purple-700">
                  {cartItems.length > 0 ? cartItems[0].name : "Your items"}
                </span>{" "}
                has been placed successfully!
              </p>

              <div className="bg-purple-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 border border-purple-200">
                <p className="text-xs sm:text-sm text-purple-700 mb-1">
                  We've sent a confirmation to your email
                </p>
                <p className="text-[10px] sm:text-xs text-purple-700">
                  {userInfo?.email || "your@email.com"}
                </p>
              </div>

              <button
                onClick={handleOrderComplete}
                className="w-full py-2 sm:py-3 px-4 sm:px-6 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-medium rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg text-sm sm:text-base"
              >
                Close
              </button>

              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Order Summary
                </h3>
                {cartItems.map((item, idx) => (
                  <p key={idx} className="text-[10px] sm:text-xs text-gray-500">
                    {item.name} × {item.quantity}
                  </p>
                ))}
                <p className="text-xs sm:text-sm font-medium text-gray-800 mt-1">
                  Total: {currency}
                  {finalTotalPrice.toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>

</>
  );
};

export default OrderWithCartsPopup;
